import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Space, Popconfirm, message, Typography, Tag, Empty } from 'antd';
import { Trash2, Landmark, Plus, RefreshCw, Edit } from 'lucide-react';
import { Method_POST_CONTEXTO } from '../../../../../service/api';

const { Title, Text } = Typography;

export default function GerenciarContasModal({ open, onClose, onNewAccount, onEditAccount, onUpdate }) {
    const [loading, setLoading] = useState(false);
    const [contas, setContas] = useState([]);

    useEffect(() => {
        if (open) {
            fetchContas();
        }
    }, [open]);

    const fetchContas = async () => {
        setLoading(true);
        try {
            const res = await Method_POST_CONTEXTO("/cliente/conta-bancaria/listar");
            setContas(Array.isArray(res) ? res : []);
        } catch (error) {
            console.error("Erro ao carregar contas:", error);
            message.error("Não foi possível carregar a lista de contas.");
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async (id) => {
        try {
            await Method_POST_CONTEXTO("/cliente/conta-bancaria/remover", { id });
            message.success("Conta removida com sucesso!");
            fetchContas();
            onUpdate?.(); // Notifica o componente pai para atualizar o select do header
        } catch (error) {
            console.error(error);
            message.error("Erro ao remover conta. Verifique se existem lançamentos vinculados.");
        }
    };

    const columns = [
        {
            title: 'Banco / Instituição',
            key: 'banco',
            render: (_, record) => (
                <Space>
                    <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        backgroundColor: '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#64748b'
                    }}>
                        <Landmark size={18} />
                    </div>
                    <div>
                        <Text strong style={{ display: 'block', fontSize: '13px' }}>
                            {record.banco?.nome || record.nome || 'Banco'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                            Código: {record.banco?.codigoFEBRABAN || '---'}
                        </Text>
                    </div>
                </Space>
            )
        },
        {
            title: 'Agência',
            key: 'agencia',
            render: (_, record) => (
                <Text style={{ fontSize: '13px' }}>
                    {record.agencia}{record.agenciaDV ? `-${record.agenciaDV}` : ''}
                </Text>
            )
        },
        {
            title: 'Conta Corrente',
            key: 'conta',
            render: (_, record) => (
                <Tag color="blue" style={{ borderRadius: 6, fontWeight: 600 }}>
                    {record.conta}{record.contaDV ? `-${record.contaDV}` : ''}
                </Tag>
            )
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<Edit size={16} />}
                        onClick={() => {
                            onClose();
                            onEditAccount(record);
                        }}
                        style={{ color: '#2563eb' }}
                        className="btn-action-edit"
                    />
                    <Popconfirm
                        title="Remover Conta"
                        description="Deseja realmente excluir esta conta? Esta ação não poderá ser desfeita."
                        onConfirm={() => handleRemove(record.id)}
                        okText="Sim, Remover"
                        cancelText="Cancelar"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="text"
                            danger
                            icon={<Trash2 size={16} />}
                            className="btn-action-delete"
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={750}
            className="premium-modal-v2 gerenciar-contas-modal"
            destroyOnClose
        >
            <div className="gerenciar-contas-content" style={{ padding: '8px' }}>
                <header className="modal-header-responsive" style={{
                    marginBottom: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #f1f5f9',
                    paddingBottom: 20,
                    gap: 16
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div className="header-icon-container" style={{
                            width: 48,
                            height: 48,
                            borderRadius: 12,
                            backgroundColor: '#f0fdf4',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#16a34a',
                            fontSize: 24,
                            flexShrink: 0
                        }}>
                            <Landmark size={24} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <Title level={4} style={{ margin: 0, color: '#0f172a', fontWeight: 700, fontSize: 'clamp(16px, 4vw, 20px)' }}>Minhas Contas</Title>
                            <Text type="secondary" style={{ fontSize: '12px' }}>Gerencie suas instituições bancárias cadastradas.</Text>
                        </div>
                    </div>
                    <Space className="header-actions">
                        <Button
                            icon={<RefreshCw size={16} />}
                            onClick={fetchContas}
                            loading={loading}
                        />
                        <Button
                            type="primary"
                            icon={<Plus size={16} />}
                            onClick={() => {
                                onClose();
                                onNewAccount();
                            }}
                            className="btn-add-account"
                            style={{
                                borderRadius: 10,
                                background: '#0f172a',
                                borderColor: '#0f172a'
                            }}
                        >
                            Adicionar Nova
                        </Button>
                    </Space>
                </header>

                <div className="table-wrapper-responsive">
                    <Table
                        dataSource={contas}
                        columns={columns}
                        rowKey="id"
                        loading={loading}
                        pagination={false}
                        className="premium-table"
                        scroll={{ x: 600 }}
                        locale={{
                            emptyText: (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="Nenhuma conta cadastrada"
                                >
                                    <Button type="primary" onClick={onNewAccount}>Cadastrar Primeira Conta</Button>
                                </Empty>
                            )
                        }}
                    />
                </div>
            </div>
            <style>{`
                .btn-action-delete:hover {
                    background-color: #fef2f2 !important;
                }
                .btn-action-edit:hover {
                    background-color: #eff6ff !important;
                }
                
                @media (max-width: 576px) {
                    .gerenciar-contas-content {
                        padding: 0 !important;
                    }
                    .modal-header-responsive {
                        flex-direction: column;
                        align-items: flex-start !important;
                        gap: 16px !important;
                    }
                    .header-actions {
                        width: 100%;
                        justify-content: space-between;
                    }
                    .btn-add-account {
                        flex: 1;
                    }
                    .table-wrapper-responsive {
                        margin: 0 -8px;
                    }
                    .premium-modal-v2 .ant-modal-content {
                        padding: 16px !important;
                    }
                }
            `}</style>
        </Modal>
    );
}
